/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { HealthCheckModel } from '../../../shared/models/healthcheck.model';
import { HealthChecksApiService } from '../../../shared/http';
import { LogService, ModalService, NotifyService, RbacService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-health-checks',
    templateUrl: './health-checks.component.html',
    styleUrls: ['./health-checks.component.scss']
})
export class HealthChecksComponent extends BaseComponent {
    /**
     * List of table rows.
     */
    public tableRows: any[] = [];

    /**
     * Store button permissions.
     */
    public permissionsLookup: any = {};

    constructor(
        private healthChecksApiService: HealthChecksApiService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Open confirm delete modal.
     * @param id
     */
    public confirmDelete(id: any): void {
        const confirmationObject = {
            title: 'confirm_delete',
            message: 'health_check_confirm_delete_text',
            callBack: this.deleteHealthcheck.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete non-built-in healthcheck scripts.
     * @param id
     * @private
     */
    private deleteHealthcheck(id: string): Observable<any> {
        this.notifyService.muteLocalNotifications();

        return new Observable<any>((observer) => {
            this.healthChecksApiService.deleteHealthCheck(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                        this.setUpPageData();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                        this.logService.error(
                            'HealthChecksComponent: deleteHealthcheck Failed to delete healthcheck - ',
                            error.message
                        );
                        this.notifyService.error('Error deleting healthcheck - ', error.message);
                    }
                });
        });
    }

    /**
     * Get page data from api.
     * @private
     */
    private setUpPageData(): void {
        this.healthChecksApiService.getAllHealthChecks()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.populateTableRows(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HealthChecksComponent: setUpPageData error getting data - ', error.message);
                }
            });
    }

    /**
     * Populate table rows.
     * @param healthChecks
     * @private
     */
    private populateTableRows(healthChecks: HealthCheckModel[]): void {
        this.tableRows = [];
        for (const check of healthChecks) {
            this.tableRows.push({
                id: check.id,
                name: check.name,
                description: check.description,
                services: this.formatServiceList(check.services),
                script: check.script,
                builtin: check.builtin,
                usage: check.usage
            });
        }
    }

    /**
     * Format the services in the view.
     * @param services
     * @private
     */
    private formatServiceList(services: any[]): string {
        const formattedServiceList = [];
        services.forEach(item => {
            formattedServiceList.push(this.formatServiceName(item));
        });

        return formattedServiceList.join(', ');
    }

    /**
     * Format the names of the services.
     * @param name
     * @private
     */
    private formatServiceName(name: string): string {
        let formattedName: string;
        if (name === 'gslb') {
            formattedName = name.toUpperCase();
        } else {
            formattedName = name.charAt(0).toUpperCase() + name.slice(1);
        }

        return formattedName;
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'healthchecks-new': [{ path: '/healthchecks', methods: ['POST'] }],
            'healthchecks-edit': [{ path: '/healthchecks/:id', methods: ['PUT'] }],
            'healthchecks-delete': [{ path: '/healthchecks/:id', methods: ['DELETE'] }],
            'healthchecks-duplicate': [{ path: '/healthchecks', methods: ['POST'] }],
            'healthchecks-view': [{ path: '/healthchecks/:id', methods: ['GET'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
